import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Rating,
  Typography,
} from "@mui/material";
import { grey, orange } from "@mui/material/colors";
import { useEffect, useState } from "react";

import TypingLoader from "features/Home/Sidebar/ChatHelper/TypingLoader";

import { BrandStatus, BrandVerificationData } from "../schema";

interface Props {
  brandData: BrandVerificationData;
  onDelete: (googlePlaceBrandId: number) => void;
}

const loadingMessages = [
  "Verifying website",
  "Searching contacts",
  "Analyzing brand data",
  "Checking social media",
  "Finalizing details",
];

export function PendingItem({ brandData, onDelete }: Props) {
  const place = brandData.googlePlaceBrand;
  const brandStatus = brandData.status;

  const [messageIndex, setMessageIndex] = useState(0);

  const startLoadingMessageCycle = () => {
    const randomInterval = Math.floor(Math.random() * (5000 - 3000 + 1)) + 3000;

    const interval = setInterval(() => {
      setMessageIndex((prev) => {
        const next = (prev + 1) % loadingMessages.length;
        return next;
      });
    }, randomInterval);

    return interval;
  };

  const getPriceLevel = () => {
    if (place.priceLevel === google.maps.places.PriceLevel.FREE) {
      return "Free";
    } else if (place.priceLevel === google.maps.places.PriceLevel.INEXPENSIVE) {
      return "$";
    } else if (place.priceLevel === google.maps.places.PriceLevel.MODERATE) {
      return "$$";
    } else if (place.priceLevel === google.maps.places.PriceLevel.EXPENSIVE) {
      return "$$$";
    } else if (
      place.priceLevel === google.maps.places.PriceLevel.VERY_EXPENSIVE
    ) {
      return "$$$$";
    }
  };

  useEffect(() => {
    startLoadingMessageCycle();
  }, []);

  return (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        border: `1px solid ${grey[300]}`,
        borderColor: grey[300],
        position: "relative",
      }}
    >
      <IconButton
        sx={{
          color: "primary.main",
          fontSize: 14,
          position: "absolute",
          right: 10,
          top: 10,
          zIndex: 5,
        }}
        onClick={() => onDelete(brandData.googlePlaceBrand.id)}
      >
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>
      <Grid container>
        <Grid item xs>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <CardContent
              sx={{
                flex: "1 0 auto",
                opacity: brandStatus === BrandStatus.loading ? 0.5 : 1,
              }}
            >
              <Grid container alignItems="center">
                <Grid item xs md={12}>
                  <Typography variant="h6">{place.displayName}</Typography>
                  {place.rating && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography
                        sx={{
                          mt: 0.25,
                          color: grey[700],
                          fontSize: 12,
                        }}
                      >
                        {place.rating.toFixed(1)}
                      </Typography>
                      <Rating
                        name="read-only"
                        value={place.rating}
                        precision={0.1}
                        color="primary"
                        readOnly
                        icon={
                          <Box
                            component="span"
                            className="fa-solid fa-star"
                            sx={{ fontSize: 12 }}
                          />
                        }
                        emptyIcon={
                          <Box
                            component="span"
                            className="fa-solid fa-star"
                            sx={{ fontSize: 12 }}
                          />
                        }
                        sx={{
                          "& .MuiRating-iconFilled": {
                            color: orange[200],
                          },

                          "& .MuiRating-iconEmpty": {
                            color: grey[300],
                          },
                        }}
                        size="small"
                      />
                      <Typography
                        sx={{
                          mt: 0.25,
                          color: grey[700],
                          fontSize: 12,
                        }}
                        color="textSecondary"
                      >
                        ({place.userRatingCount})
                      </Typography>
                      {place.priceLevel && (
                        <Typography
                          sx={{
                            mt: 0.25,
                            color: grey[700],
                            fontSize: 12,
                          }}
                          color="textSecondary"
                        >
                          · {getPriceLevel()}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Typography
                    color="textSecondary"
                    paragraph
                    sx={{
                      mt: 0.25,
                      color: grey[700],
                      fontSize: 12,
                      textTransform: "capitalize",
                    }}
                  >
                    {place.primaryTypeDisplayName} · {place.formattedAddress}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Grid container gap={1} alignItems="center">
                    {brandStatus === BrandStatus.loading && (
                      <TypingLoader title={loadingMessages[messageIndex]} />
                    )}
                    {brandStatus === BrandStatus.contactsNotFound && (
                      <Chip
                        label="Could not find contacts"
                        size="small"
                        color="warning"
                        sx={{ fontSize: 12 }}
                      />
                    )}
                    {brandStatus === BrandStatus.contactsFound && (
                      <>
                        <Chip
                          label="Found Contacts"
                          size="small"
                          color="success"
                          sx={{ fontSize: 12 }}
                        />
                        <Typography
                          color="textSecondary"
                          variant="caption"
                          sx={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            WebkitLineClamp: 1,
                            textOverflow: "ellipsis",
                          }}
                        >
                          {brandData.bentoContacts
                            ?.map((b) => b.email)
                            .join(", ")}
                        </Typography>
                      </>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Box>
        </Grid>
      </Grid>
    </Card>
  );
}
